import { ComponentType, forwardRef, ReactElement, ReactNode } from 'react';

import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import ListItem, { ListItemProps } from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { styled, useThemeProps, SxProps, Theme, Breakpoint } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

export interface GratiDirectoryItemBadge {
  Icon: ComponentType<SvgIconProps>;
  title: string;
}

export interface GratiDirectoryItemSlots {
  color?: string;
  icon?: ReactElement;
  badge?: GratiDirectoryItemBadge;
  avatar1?: ReactElement;
  avatar2?: ReactElement;
  primary?: ReactNode;
  secondary?: ReactNode;
  tertiary?: ReactNode;
  column2?: ReactNode;
  action?: ReactElement;
}

export interface GratiDirectoryItemSlotProps {
  icon?: {
    sx?: SxProps<Theme>;
    isHiddenOnAction?: boolean;
    hideBreakpoint?: Breakpoint;
  };
  badge?: {
    sx?: SxProps<Theme>;
  };
  avatar1?: {
    sx?: SxProps<Theme>;
    isHiddenOnAction?: boolean;
    hideBreakpoint?: Breakpoint;
  };
  avatar2?: {
    sx?: SxProps<Theme>;
    isHiddenOnAction?: boolean;
    hideBreakpoint?: Breakpoint;
  };
  primary?: {
    sx?: SxProps<Theme>;
    hideBreakpoint?: Breakpoint;
  };
  secondary?: {
    sx?: SxProps<Theme>;
    hideBreakpoint?: Breakpoint;
  };
  tertiary?: {
    sx?: SxProps<Theme>;
    hideBreakpoint?: Breakpoint;
  };
  column2?: {
    sx?: SxProps<Theme>;
    hideBreakpoint?: Breakpoint;
  };
  action?: {
    sx?: SxProps<Theme>;
  };
}

export interface GratiDirectoryItemColumn {
  content: ReactElement;
  // Fixed width per breakpoint — use this to align columns across rows.
  // Accepts a plain string ('12rem') or a responsive object ({ md: '12rem', lg: '16rem' }).
  width?: string | Partial<Record<Breakpoint, string>>;
  minWidth?: string;    // minimum width only — column can grow beyond this
  hideBreakpoint?: Breakpoint;
  // When true, this column can shrink (truncating its content) but does not grow.
  // Extra space goes to the right of the rightmost column, before the action slot.
  // Use on the primary content column (e.g. email) to prevent overflow on narrow screens.
  flex?: boolean;
}

export interface GratiDirectoryItemProps extends Omit<ListItemProps, 'slots' | 'slotProps' | 'onClick'> {
  // Navigation
  to?: string;
  onClick?: () => void;

  // Column-based layout (new API — use instead of slots for multi-column rows)
  columns?: GratiDirectoryItemColumn[];

  // Slots
  slots?: GratiDirectoryItemSlots;
  slotProps?: GratiDirectoryItemSlotProps;

  // State
  isActionOpen?: boolean;
}

interface GratiDirectoryItemOwnerState extends GratiDirectoryItemProps {}

const GratiDirectoryItemRoot = styled(ListItem, {
  name: 'GratiDirectoryItem',
  slot: 'root',
  overridesResolver: (props, styles) => {
    return styles.root;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

const shouldForwardProp = (prop: string) => prop !== 'ownerState';

const GratiDirectoryItemIcon = styled(ListItemAvatar, {
  name: 'GratiDirectoryItem',
  slot: 'icon',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.icon;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

const GratiDirectoryItemAvatar = styled(ListItemAvatar, {
  name: 'GratiDirectoryItem',
  slot: 'avatar',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.avatar;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

const GratiDirectoryItemContent = styled(Box, {
  name: 'GratiDirectoryItem',
  slot: 'content',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.content;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

const GratiDirectoryItemPrimary = styled(Typography, {
  name: 'GratiDirectoryItem',
  slot: 'primary',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.primary;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

const GratiDirectoryItemSecondary = styled('div', {
  name: 'GratiDirectoryItem',
  slot: 'secondary',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.secondary;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(({ theme }) => ({
  ...theme.typography.bodyMedium,
}));

const GratiDirectoryItemTertiary = styled('div', {
  name: 'GratiDirectoryItem',
  slot: 'tertiary',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.tertiary;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(({ theme }) => ({
  ...theme.typography.bodySmall,
}));

const GratiDirectoryItemColumn2 = styled(Box, {
  name: 'GratiDirectoryItem',
  slot: 'column2',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.column2;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

const GratiDirectoryItemAction = styled(Box, {
  name: 'GratiDirectoryItem',
  slot: 'action',
  shouldForwardProp,
  overridesResolver: (props, styles) => {
    return styles.action;
  },
})<{ ownerState: GratiDirectoryItemOwnerState }>(() => ({}));

export default forwardRef<HTMLLIElement, GratiDirectoryItemProps>(function GratiDirectoryItem(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'GratiDirectoryItem' });

  const {
    to,
    onClick,
    columns,
    slots,
    slotProps,
    isActionOpen = false,
    sx,
    className,
    ...other
  } = props;

  const ownerState = { ...props };

  // Helper to get responsive display based on slotProps
  const getResponsiveDisplay = (
    elementSlotProps?: {
      isHiddenOnAction?: boolean;
      hideBreakpoint?: Breakpoint
    }
  ) => {
    if (!elementSlotProps) return {};

    let displayValue: Record<string, string> | string = 'flex';

    // Handle hideBreakpoint
    if (elementSlotProps.hideBreakpoint) {
      const breakpoints: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];
      const hideIndex = breakpoints.indexOf(elementSlotProps.hideBreakpoint);

      const breakpointDisplay: Record<string, string> = {};
      breakpoints.forEach((bp, index) => {
        breakpointDisplay[bp] = index <= hideIndex ? 'none' : 'flex';
      });
      displayValue = breakpointDisplay;
    }

    // Handle isHiddenOnAction
    if (elementSlotProps.isHiddenOnAction && isActionOpen) {
      if (typeof displayValue === 'object') {
        displayValue.xs = 'none';
      } else {
        displayValue = { xs: 'none', sm: 'flex' };
      }
    }

    return { display: displayValue };
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const innerContent = (
    <>
      {slots?.icon && (
        <GratiDirectoryItemIcon
          ownerState={ownerState}
          sx={{
            minWidth: 'auto',
            ...getResponsiveDisplay(slotProps?.icon),
            ...slotProps?.icon?.sx
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {slots.icon}
            {slots.badge && (
              <Tooltip title={slots.badge.title} arrow placement="right">
                <Box
                  className="GratiDirectoryItemBadge-container"
                  sx={{
                    ...slotProps?.badge?.sx
                  }}
                >
                  <slots.badge.Icon className="GratiDirectoryItemBadge-icon" />
                </Box>
              </Tooltip>
            )}
          </Box>
        </GratiDirectoryItemIcon>
      )}

      {slots?.avatar1 && (
        <GratiDirectoryItemAvatar
          ownerState={ownerState}
          sx={{
            minWidth: 'auto',
            ...getResponsiveDisplay(slotProps?.avatar1),
            ...slotProps?.avatar1?.sx
          }}
        >
          {slots.avatar1}
        </GratiDirectoryItemAvatar>
      )}

      {slots?.avatar2 && (
        <GratiDirectoryItemAvatar
          ownerState={ownerState}
          sx={{
            minWidth: 'auto',
            ...getResponsiveDisplay(slotProps?.avatar2),
            ...slotProps?.avatar2?.sx
          }}
        >
          {slots.avatar2}
        </GratiDirectoryItemAvatar>
      )}

      {(slots?.primary || slots?.secondary || slots?.tertiary) && (
        <GratiDirectoryItemContent ownerState={ownerState} sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {slots.primary && (
            <GratiDirectoryItemPrimary
              ownerState={ownerState}
              variant="titleMedium"
              sx={{
                ...getResponsiveDisplay(slotProps?.primary),
                ...slotProps?.primary?.sx
              }}
            >
              {slots.primary}
            </GratiDirectoryItemPrimary>
          )}
          {slots.secondary && (
            <GratiDirectoryItemSecondary
              ownerState={ownerState}
              sx={{
                mt: 0.25,
                color: 'text.secondary',
                ...getResponsiveDisplay(slotProps?.secondary),
                ...slotProps?.secondary?.sx
              }}
            >
              {slots.secondary}
            </GratiDirectoryItemSecondary>
          )}
          {slots.tertiary && (
            <GratiDirectoryItemTertiary
              ownerState={ownerState}
              sx={{
                mt: 0.25,
                color: 'text.secondary',
                ...getResponsiveDisplay(slotProps?.tertiary),
                ...slotProps?.tertiary?.sx
              }}
            >
              {slots.tertiary}
            </GratiDirectoryItemTertiary>
          )}
        </GratiDirectoryItemContent>
      )}
      {slots?.column2 && (
        <GratiDirectoryItemColumn2
          ownerState={ownerState}
          sx={{
            flexShrink: 0,
            alignSelf: 'center',
            ...getResponsiveDisplay(slotProps?.column2),
            ...slotProps?.column2?.sx
          }}
        >
          {slots.column2}
        </GratiDirectoryItemColumn2>
      )}
    </>
  );

  const columnsContent = columns ? (
    <>
      {columns.map((col, i) => (
        <Box
          key={i}
          sx={{
            alignSelf: 'center',
            py: 1,
            overflow: 'hidden',
            // flex columns shrink (truncate) but do not grow — extra space stays
            // to the right of the rightmost column, before the action slot.
            // All other columns are fixed (no shrink, no grow).
            ...(col.flex
              ? { flex: '0 1 auto', minWidth: 0 }
              : { flexShrink: 0 }),
            ...(col.width ? { width: col.width, maxWidth: col.width } : {}),
            ...(col.minWidth ? { minWidth: col.minWidth } : {}),
            ...(col.hideBreakpoint
              ? getResponsiveDisplay({ hideBreakpoint: col.hideBreakpoint })
              : {}),
          }}
        >
          {col.content}
        </Box>
      ))}
    </>
  ) : null;

  const colorStripe = slots?.color ? (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '0.25rem',
        backgroundColor: slots.color,
        borderRadius: '0.25rem 0 0 0.25rem',
      }}
    />
  ) : null;

  const listItemSx: SxProps<Theme> = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    px: 2,
    ...(isActionOpen && {
      backgroundColor: 'action.selected',
    }),
    ...sx
  };

  if (to) {
    return (
      <GratiDirectoryItemRoot
        ref={ref}
        ownerState={ownerState}
        className={`${className || ''} ${isActionOpen ? 'Mui-selected' : ''}`.trim()}
        sx={listItemSx}
        {...other}
      >
        {colorStripe}
        <Box
          component={Link}
          to={to}
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
            alignItems: 'center',
            cursor: 'pointer',
            gap: 2,
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {columns ? columnsContent : innerContent}
        </Box>
        {slots?.action && (
          <GratiDirectoryItemAction
            ownerState={ownerState}
            onClick={(e) => {
              // Only stop propagation to prevent navigation, but don't preventDefault
              // so that interactive elements (checkboxes, buttons) work correctly
              e.stopPropagation();
            }}
            sx={slotProps?.action?.sx}
          >
            {slots.action}
          </GratiDirectoryItemAction>
        )}
      </GratiDirectoryItemRoot>
    );
  }

  return (
    <GratiDirectoryItemRoot
      ref={ref}
      ownerState={ownerState}
      className={`${className || ''} ${isActionOpen ? 'Mui-selected' : ''}`.trim()}
      sx={listItemSx}
      {...other}
    >
      {colorStripe}
      <Box
        sx={{
          display: 'flex',
          flex: '1 1 auto',
          minWidth: 0,
          alignItems: 'center',
          cursor: onClick ? 'pointer' : 'default',
          gap: 2,
        }}
        onClick={handleClick}
      >
        {columns ? columnsContent : innerContent}
      </Box>
      {slots?.action && (
        <GratiDirectoryItemAction
          ownerState={ownerState}
          onClick={(e) => {
            // Only stop propagation to prevent navigation, but don't preventDefault
            // so that interactive elements (checkboxes, buttons) work correctly
            e.stopPropagation();
          }}
          sx={slotProps?.action?.sx}
        >
          {slots.action}
        </GratiDirectoryItemAction>
      )}
    </GratiDirectoryItemRoot>
  );
});
